// Google Analytics 4 with Consent Mode v2 (GDPR/RGPD compliant).
//
// The Measurement ID can come from either source: the build-time REACT_APP_GA_ID
// env var, or the runtime "GA4 Measurement ID" field in /admin/settings (SEO tab)
// — the latter takes priority so a non-technical admin can change it without a
// redeploy. With no ID from either source the whole module is inert — no
// scripts, no banner — so the site stays clean until analytics is configured.
//
// Flow: on load we set consent defaults to "denied" and load gtag.js. Google
// only sets analytics cookies once the visitor accepts; before that it sends
// cookieless, modelled pings. The banner flips analytics_storage to granted.

const ENV_GA_ID = process.env.REACT_APP_GA_ID || '';

const CONSENT_KEY = 'owa-cookie-consent'; // 'granted' | 'denied'

export function getStoredConsent() {
  try {
    return localStorage.getItem(CONSENT_KEY);
  } catch {
    return null;
  }
}

function gtag() {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments);
}

let initialised = false;
let activeGaId = '';

// Load gtag.js once and register Consent Mode defaults (denied until accepted).
// `overrideId` is the runtime value from /api/settings/seo, if any; falls back
// to the build-time env var.
export function initAnalytics(overrideId) {
  const gaId = (overrideId || ENV_GA_ID || '').trim();
  if (!gaId || initialised || typeof window === 'undefined') return;
  initialised = true;
  activeGaId = gaId;

  window.gtag = window.gtag || gtag;
  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    wait_for_update: 500,
  });

  const stored = getStoredConsent();
  if (stored === 'granted') {
    window.gtag('consent', 'update', { analytics_storage: 'granted' });
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  document.head.appendChild(script);

  window.gtag('js', new Date());
  // We send page_view manually on each route change (SPA), so disable the
  // automatic one to avoid double counting.
  window.gtag('config', gaId, { send_page_view: false });
}

export function setConsent(granted) {
  try {
    localStorage.setItem(CONSENT_KEY, granted ? 'granted' : 'denied');
  } catch {
    /* ignore storage errors */
  }
  if (activeGaId && window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: granted ? 'granted' : 'denied',
    });
  }
}

export function trackPageView({ path, title, lang }) {
  if (!activeGaId || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: title || document.title,
    language: lang,
  });
}
